import {
  GetUsersResponse,
  OrganizationUnitNameAndId,
  UserStatus,
  UserType,
} from '@inplay/models';
import { startCase } from 'lodash';
import { ReactNode } from 'react';
import { Option } from '../dropDownInput/dropDownInput.type';
import { UserManagementFilters } from './type';

/**
 * Renders a labeled form row with a consistent layout.
 *
 * @param label - The label text displayed on the left side of the row.
 * @param children - The form element (e.g., input, dropdown, date picker) displayed on the right side.
 * @returns A JSX element representing a horizontal form row with label and input.
 *
 * Layout: 30% width for the label, 70% width for the input.
 * Adds top margin except for the first row.
 */
export const renderFormRow = (label: ReactNode, children: ReactNode) => (
  <div className="flex mt-4 items-start">
    <div className="w-[30%] pt-2 text-sm text-[#1A1A1A] leading-[20px] min-h-[40px]">
      {label}
    </div>
    <div className="w-[70%] min-h-[40px]">{children}</div>
  </div>
);

export const userTypeOptions: Option[] = [
  {
    label: startCase(UserType.ORGANIZATION_ADMIN),
    value: UserType.ORGANIZATION_ADMIN,
  },
  {
    label: startCase(UserType.SUB_ORGANIZATION_ADMIN),
    value: UserType.SUB_ORGANIZATION_ADMIN,
  },
  {
    label: startCase(UserType.VENUE_ADMIN),
    value: UserType.VENUE_ADMIN,
  },
  {
    label: startCase(UserType.VENUE_MANAGER),
    value: UserType.VENUE_MANAGER,
  },
  {
    label: startCase(UserType.VENUE_STAFF),
    value: UserType.VENUE_STAFF,
  },
];

export const userStatusOption: Option[] = [
  { label: startCase(UserStatus.ACTIVE), value: UserStatus.ACTIVE },
  { label: startCase(UserStatus.INACTIVE), value: UserStatus.INACTIVE },
  { label: startCase(UserStatus.INVITED), value: UserStatus.INVITED },
];

export const venueStatusOption: Option[] = [
  { label: startCase(UserStatus.ACTIVE), value: UserStatus.ACTIVE },
  { label: startCase(UserStatus.INACTIVE), value: UserStatus.INACTIVE },
];

/**
 * Filters the list of users based on selected filter criteria:
 * - User type (can be multiple)
 * - User status
 * - Venue branch
 * - Date range (based on user's addedBy.date)
 *
 * @param users - The full list of users to filter
 * @param userType - Array of selected user types (e.g., ['admin', 'viewer'])
 * @param userStatus - Selected user status (e.g., 'active', 'inactive')
 * @param venueBranch - Selected venue branch in camelCase format
 * @param dateRange - Object with 'from' and 'to' Date values for filtering by date
 * @returns A filtered array of users matching the provided filter criteria
 */
export const getFilteredUsers = (
  users: GetUsersResponse[],
  { userStatus, userType, venueBranches, dateRange }: UserManagementFilters,
): GetUsersResponse[] => {
  return users.filter((user: GetUsersResponse) => {
    const matchesType: boolean =
      userType.length > 0 ? userType.includes(user.type) : true;

    const matchesStatus: boolean =
      userStatus.length > 0 ? userStatus.includes(user.status) : true;

    const matchesBranch: boolean =
      venueBranches && venueBranches.length > 0
        ? Array.isArray(user?.venueBranchs) &&
          user.venueBranchs.some((branch: OrganizationUnitNameAndId) =>
            venueBranches.includes(branch.id),
          )
        : true;

    const userDateRaw: Date | undefined = user.addedBy?.date;

    let inDateRange = true;
    if (dateRange?.from && dateRange?.to && userDateRaw) {
      const fromDate = new Date(dateRange.from);
      const toDate = new Date(dateRange.to);
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);

      const userDate = new Date(userDateRaw);
      inDateRange = userDate >= fromDate && userDate <= toDate;
    }

    return matchesType && matchesStatus && matchesBranch && inDateRange;
  });
};
